import { useState, useEffect } from 'react';
import { Modal, Button, Form, Badge, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { ApiService } from '../services/api';
import { Certificate, CreateCertificateRequest, CreateCertificateResponse, CAHealthResponse } from '../types';
import { showSuccess, showError } from '../utils/toast';
import { UniversalSchemaForm } from './shared/UniversalSchemaForm';
import { UniversalSchemaTable } from './shared/UniversalSchemaTable';
import { CERTIFICATES_OPENAPI } from '../schemas';

interface CertificateManagementProps {}

// Certificate form customizations to match the original certificate parser exactly
const CERTIFICATE_CUSTOM_LABELS = {
  commonName: 'Common Name',
  expiryDate: 'Expiry Date',
  certificateType: 'Certificate Type',
  keyUsage: 'Key Usage',
  environment: 'Environment',
  createdBy: 'Created By',
  subjectAlternativeNames: 'Subject Alternative Names (SANs)',
  organizationName: 'Organization Name',
  organizationalUnit: 'Organizational Unit',
  country: 'Country Code',
  state: 'State/Province',
  locality: 'City/Locality',
  reason: 'Revocation Reason',
  revokedBy: 'Revoked By'
};

const CERTIFICATE_CUSTOM_PLACEHOLDERS = {
  commonName: 'e.g., client.example.com',
  organizationName: 'e.g., Rail Delivery Group',
  organizationalUnit: 'e.g., IT Department',
  country: 'e.g., GB',
  state: 'e.g., England',
  locality: 'e.g., London',
  subjectAlternativeNames: 'One per line, e.g.:\napi.example.com\nwww.example.com'
};

const CERTIFICATE_CUSTOM_PATTERNS = {
  email: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
  notificationEmail: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
  commonName: '^[a-zA-Z0-9]([a-zA-Z0-9\\-]{0,61}[a-zA-Z0-9])?(\\.[a-zA-Z0-9]([a-zA-Z0-9\\-]{0,61}[a-zA-Z0-9])?)*$',
  country: '^[A-Z]{2}$',
  organizationName: '^[a-zA-Z0-9\\s\\-\\.\\,\\&]+$',
  organizationalUnit: '^[a-zA-Z0-9\\s\\-\\.\\,\\&]+$',
  state: '^[a-zA-Z\\s\\-\\.]+$',
  locality: '^[a-zA-Z\\s\\-\\.]+$',
  contactNumber: '^[\\+]?[0-9\\s\\-\\(\\)]{7,20}$'
};

const CERTIFICATE_CUSTOM_DEFAULTS = {
  country: 'GB'
};

export function CertificateManagement({}: CertificateManagementProps) {
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showPassphraseModal, setShowPassphraseModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState<Certificate | null>(null);
  const [createResponse, setCreateResponse] = useState<CreateCertificateResponse | null>(null);
  const [caHealth, setCAHealth] = useState<CAHealthResponse | null>(null);
  const [environmentFilter, setEnvironmentFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [currentUser, setCurrentUser] = useState<string>('');
  const [createLoading, setCreateLoading] = useState(false);

  // Revocation form state
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [revokeFormErrors, setRevokeFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadCertificates();
    loadCAHealth();
    getCurrentUser();
  }, [environmentFilter, statusFilter]);

  const getCurrentUser = async () => {
    try {
      const user = await ApiService.getCurrentUser();
      if (user) {
        setCurrentUser(user.username);
      }
    } catch (error) {
      console.error('Failed to get current user:', error);
    }
  };

  const loadCertificates = async () => {
    try {
      setLoading(true);
      const response = await ApiService.listCertificates(
        (statusFilter as 'active' | 'revoked' | 'expired' | undefined) || undefined,
        (environmentFilter as 'prod' | 'non-prod' | undefined) || undefined,
        50
      );
      setCertificates(response.certificates || []);
    } catch (error) {
      showError('Failed to load certificates');
      console.error('Error loading certificates:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCAHealth = async () => {
    try {
      const health = await ApiService.getCAHealth();
      setCAHealth(health);
    } catch (error) {
      console.error('Failed to load CA health:', error);
    }
  };

  const handleCreateCertificate = async (formData: Record<string, any>) => {
    // Prevent multiple submissions
    if (createLoading) return;

    try {
      setCreateLoading(true);

      // Format the request data according to OpenAPI schema
      const requestData: CreateCertificateRequest = {
        commonName: formData.commonName,
        expiryDate: formData.expiryDate, // Already in ISO format from datetime input
        certificateType: formData.certificateType as 'client' | 'server',
        keyUsage: ['digitalSignature', 'keyEncipherment'], // Default key usage
        environment: formData.environment as 'prod' | 'non-prod',
        createdBy: currentUser,
        // Handle subjectAlternativeNames - convert textarea to array
        subjectAlternativeNames: formData.subjectAlternativeNames 
          ? formData.subjectAlternativeNames.split('\n').map((san: string) => san.trim()).filter((san: string) => san !== '')
          : [],
        organizationName: formData.organizationName || '',
        organizationalUnit: formData.organizationalUnit || '',
        country: formData.country || 'GB',
        state: formData.state || '',
        locality: formData.locality || '',
      };
      
      const response = await ApiService.createCertificate(requestData);

      setCreateResponse(response);
      setShowCreateModal(false);
      setShowPassphraseModal(true);
      
      // Reload certificates
      await loadCertificates();
      
      showSuccess('Certificate created successfully!');
    } catch (error: any) {
      console.error('Error creating certificate:', error);
      showError(error?.message || 'Failed to create certificate');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCreateModalOpen = () => {
    setShowCreateModal(true);
  };

  const handleCreateModalClose = () => {
    setShowCreateModal(false);
  };

  const handleRevokeCertificateSubmit = async (formData: Record<string, any>) => {
    if (!selectedCertificate) return;

    // Prevent multiple submissions
    if (revokeLoading) return;

    // Clear previous errors
    setRevokeFormErrors({});

    try {
      setRevokeLoading(true);
      
      await ApiService.revokeCertificate(selectedCertificate.certificateId!, {
        reason: formData.reason,
        revokedBy: formData.revokedBy || currentUser, // Use form data or fallback to currentUser
      });

      setShowRevokeModal(false);
      setSelectedCertificate(null);
      await loadCertificates();
      
      showSuccess('Certificate revoked successfully');
    } catch (error: any) {
      console.error('Error revoking certificate:', error);
      
      // Try to map server errors to form fields
      if (error?.message) {
        const errorMessage = error.message.toLowerCase();
        const errors: Record<string, string> = {};
        
        if (errorMessage.includes('reason')) {
          errors.reason = error.message;
        } else {
          // Generic error - show as toast since we can't map it to a specific field
          showError(error.message || 'Failed to revoke certificate');
        }
        
        if (Object.keys(errors).length > 0) {
          setRevokeFormErrors(errors);
        }
      } else {
        showError('Failed to revoke certificate');
      }
    } finally {
      setRevokeLoading(false);
    }
  };

  const handleDownloadCertificate = async (certificate: Certificate) => {
    try {
      const response = await ApiService.downloadCertificate(certificate.certificateId!);
      if (response.downloadUrl) {
        // Open download URL in new tab
        window.open(response.downloadUrl, '_blank');
        showSuccess('Download started');
      } else {
        showError('No download available for this certificate');
      }
    } catch (error: any) {
      showError(error?.message || 'Failed to get download URL');
      console.error('Error downloading certificate:', error);
    }
  };

  const handleRevokeCertificate = (certificate: Certificate) => {
    setSelectedCertificate(certificate);
    setShowRevokeModal(true);
  };

  const handleViewCertificate = (certificate: Certificate) => {
    setSelectedCertificate(certificate);
    setShowViewModal(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      showSuccess('Copied to clipboard!');
    }).catch(() => {
      showError('Failed to copy to clipboard');
    });
  };

  // SAN management functions - commented out as mostly using simple client certificates
  /*
  const addSAN = () => {
    setFormData((prev: CreateCertificateRequest) => ({
      ...prev,
      subjectAlternativeNames: [...(prev.subjectAlternativeNames || []), '']
    }));
  };

  const updateSAN = (index: number, value: string) => {
    setFormData((prev: CreateCertificateRequest) => ({
      ...prev,
      subjectAlternativeNames: prev.subjectAlternativeNames?.map((san: string, i: number) => 
        i === index ? value : san
      ) || []
    }));
  };

  const removeSAN = (index: number) => {
    setFormData((prev: CreateCertificateRequest) => ({
      ...prev,
      subjectAlternativeNames: prev.subjectAlternativeNames?.filter((_: string, i: number) => i !== index) || []
    }));
  };
  */

  return (
    <>
      {/* CA Health Status */}
      {caHealth && (
        <div className="row mb-3">
          <div className="col-md-6">
            <div className="card card-outline card-info">
              <div className="card-header">
                <h3 className="card-title">
                  <i className="fas fa-heartbeat mr-2"></i>
                  Certificate Authority Status
                </h3>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-6">
                    <div className="text-center">
                      <h5>Production CA</h5>
                      {caHealth.environments?.prod ? (
                        <Badge bg="success" className="p-2">
                          <i className="fas fa-check mr-1"></i>
                          Healthy
                        </Badge>
                      ) : (
                        <Badge bg="danger" className="p-2">
                          <i className="fas fa-times mr-1"></i>
                          Unavailable
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-center">
                      <h5>Non-Production CA</h5>
                      {caHealth.environments?.['non-prod'] ? (
                        <Badge bg="success" className="p-2">
                          <i className="fas fa-check mr-1"></i>
                          Healthy
                        </Badge>
                      ) : (
                        <Badge bg="danger" className="p-2">
                          <i className="fas fa-times mr-1"></i>
                          Unavailable
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Certificate List */}
      <div className="card card-primary card-outline">
        <div className="card-header">
          <h3 className="card-title">
            <i className="fas fa-certificate mr-2"></i>
            Certificate Management
          </h3>
          <div className="card-tools">
            <Button 
              variant="primary" 
              size="sm"
              onClick={handleCreateModalOpen}
            >
              <i className="fas fa-plus mr-1"></i>
              Create Certificate
            </Button>
          </div>
        </div>
        <div className="card-body">
          {/* Filters */}
          <div className="row mb-3">
            <div className="col-md-3">
              <select
                className="form-control"
                value={environmentFilter}
                onChange={(e) => setEnvironmentFilter(e.target.value)}
              >
                <option value="">All Environments</option>
                <option value="prod">Production</option>
                <option value="non-prod">Non-Production</option>
              </select>
            </div>
            <div className="col-md-3">
              <select
                className="form-control"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="revoked">Revoked</option>
                <option value="expired">Expired</option>
              </select>
            </div>
            <div className="col-md-6 text-end">
              <Button variant="outline-secondary" onClick={loadCertificates}>
                <i className="fas fa-sync mr-1"></i>
                Refresh
              </Button>
            </div>
          </div>

          {/* Certificate Table */}
          <UniversalSchemaTable
            openApiSpec={CERTIFICATES_OPENAPI}
            schemaName="Certificate"
            data={certificates}
            loading={loading}
            emptyMessage="No certificates found"
            onView={handleViewCertificate}
            onEdit={undefined} // Certificates don't have edit functionality
            onDelete={undefined} // We use revoke instead of delete
            customActions={[
              {
                label: 'Download',
                icon: 'fas fa-download',
                onClick: handleDownloadCertificate
              },
              {
                label: 'Revoke',
                icon: 'fas fa-ban',
                variant: 'danger',
                onClick: handleRevokeCertificate,
                condition: (cert: Certificate) => cert.status === 'active'
              }
            ]}
            hiddenFields={[
              'keyUsage', 
              'extendedKeyUsage', 
              'subjectAlternativeNames',
              'organizationalUnit', 
              'locality', 
              'state', 
              'country',
              'certificateId' // Hide internal ID, show serial number instead
            ]}
            customLabels={{
              commonName: 'Common Name',
              certificateType: 'Type',
              serialNumber: 'Serial Number',
              issuedAt: 'Issued',
              expiresAt: 'Expires',
              createdAt: 'Created',
              updatedAt: 'Updated',
              createdBy: 'Created By',
              updatedBy: 'Updated By',
              revokedBy: 'Revoked By',
              revokedAt: 'Revoked',
              organizationName: 'Organization',
              environment: 'Environment',
              status: 'Status'
            }}
            customColumnTypes={{
              status: 'status',
              environment: 'badge',
              certificateType: 'badge',
              issuedAt: 'date',
              expiresAt: 'date',
              createdAt: 'datetime',
              updatedAt: 'datetime',
              revokedAt: 'datetime'
            }}
            striped={true}
            hover={true}
            responsive={true}
          />
        </div>
      </div>

      {/* Create Certificate Modal */}
      <Modal show={showCreateModal} onHide={handleCreateModalClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-certificate mr-2"></i>
            Create New Certificate
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <UniversalSchemaForm
            openApiSpec={CERTIFICATES_OPENAPI}
            schemaName="CreateCertificateRequest"
            onSubmit={handleCreateCertificate}
            loading={createLoading}
            showSubmitButton={false}
            formId="certificate-form"
            customLabels={CERTIFICATE_CUSTOM_LABELS}
            customPlaceholders={CERTIFICATE_CUSTOM_PLACEHOLDERS}
            customPatterns={CERTIFICATE_CUSTOM_PATTERNS}
            customDefaults={{
              ...CERTIFICATE_CUSTOM_DEFAULTS,
              createdBy: currentUser
            }}
            hiddenFields={['createdBy']}
          />
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button 
            variant="secondary" 
            onClick={handleCreateModalClose}
            disabled={createLoading}
          >
            <i className="fas fa-times mr-1"></i>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={() => {
              // Trigger form submission by finding the form and calling submit
              const form = document.querySelector('#certificate-form');
              if (form) {
                (form as HTMLFormElement).requestSubmit();
              } else {
                console.error('Certificate form not found with selector #certificate-form');
              }
            }}
            disabled={createLoading}
          >
            {createLoading ? (
              <>
                <Spinner animation="border" size="sm" className="mr-2" />
                Creating Certificate...
              </>
            ) : (
              <>
                <i className="fas fa-certificate mr-1"></i>
                Create Certificate
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Passphrase Display Modal */}
      <Modal show={showPassphraseModal} onHide={() => setShowPassphraseModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-key mr-2 text-warning"></i>
            Certificate Created Successfully
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {createResponse && (
            <>
              <Alert variant="success">
                <strong>Certificate ID:</strong> {createResponse.certificateId}
                <br />
                <strong>Serial Number:</strong> {createResponse.serialNumber}
              </Alert>

              {createResponse.passphrase && (
                <Alert variant="warning">
                  <h6><i className="fas fa-exclamation-triangle mr-2"></i>Important - Save This Passphrase</h6>
                  <p>This passphrase will only be shown once. Please copy and store it securely:</p>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      value={createResponse.passphrase}
                      readOnly
                      className="font-monospace"
                    />
                    <Button 
                      variant="outline-secondary"
                      onClick={() => copyToClipboard(createResponse.passphrase!)}
                    >
                      <i className="fas fa-copy"></i>
                    </Button>
                  </InputGroup>
                </Alert>
              )}

              {createResponse.downloadUrl && (
                <Alert variant="info">
                  <p>Your certificate bundle is ready for download:</p>
                  <Button 
                    variant="primary"
                    onClick={() => window.open(createResponse.downloadUrl, '_blank')}
                  >
                    <i className="fas fa-download mr-2"></i>
                    Download Certificate Bundle
                  </Button>
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowPassphraseModal(false);
              setCreateResponse(null);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Revoke Certificate Modal */}
      <Modal show={showRevokeModal} onHide={() => setShowRevokeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <i className="fas fa-ban mr-2"></i>
            Revoke Certificate
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCertificate && (
            <>
              <Alert variant="warning">
                <strong>Warning:</strong> This action cannot be undone. The certificate will be permanently revoked.
              </Alert>
              
              <p>
                <strong>Certificate:</strong> {selectedCertificate.commonName}<br />
                <strong>Serial Number:</strong> {selectedCertificate.serialNumber}
              </p>

              <UniversalSchemaForm
                openApiSpec={CERTIFICATES_OPENAPI}
                schemaName="RevokeCertificateRequest"
                onSubmit={handleRevokeCertificateSubmit}
                loading={revokeLoading}
                errors={revokeFormErrors}
                showSubmitButton={false}
                formId="revoke-form"
                customLabels={CERTIFICATE_CUSTOM_LABELS}
                customPlaceholders={CERTIFICATE_CUSTOM_PLACEHOLDERS}
                customPatterns={CERTIFICATE_CUSTOM_PATTERNS}
                customDefaults={{
                  ...CERTIFICATE_CUSTOM_DEFAULTS,
                  revokedBy: currentUser
                }}
                hiddenFields={['revokedBy']}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button variant="secondary" onClick={() => setShowRevokeModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={() => {
              // Trigger form submission by finding the form and calling submit
              const form = document.querySelector('#revoke-form');
              if (form) {
                (form as HTMLFormElement).requestSubmit();
              } else {
                console.error('Revoke form not found with selector #revoke-form');
              }
            }}
            disabled={revokeLoading}
          >
            {revokeLoading ? (
              <>
                <Spinner animation="border" size="sm" className="mr-2" />
                Revoking...
              </>
            ) : (
              <>
                <i className="fas fa-ban mr-1"></i>
                Revoke Certificate
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Certificate Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-certificate mr-2"></i>
            Certificate Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCertificate && (
            <div className="row">
              <div className="col-md-6">
                <h6 className="text-primary">Basic Information</h6>
                <table className="table table-sm">
                  <tbody>
                    <tr>
                      <td><strong>Common Name:</strong></td>
                      <td>{selectedCertificate.commonName}</td>
                    </tr>
                    <tr>
                      <td><strong>Serial Number:</strong></td>
                      <td><code>{selectedCertificate.serialNumber}</code></td>
                    </tr>
                    <tr>
                      <td><strong>Certificate Type:</strong></td>
                      <td>
                        <Badge bg={selectedCertificate.certificateType === 'client' ? 'info' : 'warning'}>
                          {selectedCertificate.certificateType}
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Environment:</strong></td>
                      <td>
                        <Badge bg={selectedCertificate.environment === 'prod' ? 'danger' : 'info'}>
                          {selectedCertificate.environment === 'prod' ? 'Production' : 'Non-Production'}
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Status:</strong></td>
                      <td>
                        <Badge bg={
                          selectedCertificate.status === 'active' ? 'success' :
                          selectedCertificate.status === 'expired' ? 'warning' : 'danger'
                        }>
                          {selectedCertificate.status}
                        </Badge>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="col-md-6">
                <h6 className="text-primary">Dates & Lifecycle</h6>
                <table className="table table-sm">
                  <tbody>
                    <tr>
                      <td><strong>Issued:</strong></td>
                      <td>{selectedCertificate.issuedAt ? new Date(selectedCertificate.issuedAt).toLocaleDateString() : 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>Expires:</strong></td>
                      <td>{selectedCertificate.expiresAt ? new Date(selectedCertificate.expiresAt).toLocaleDateString() : 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>Created By:</strong></td>
                      <td>{selectedCertificate.createdBy}</td>
                    </tr>
                    {selectedCertificate.revokedAt && (
                      <>
                        <tr>
                          <td><strong>Revoked:</strong></td>
                          <td>{new Date(selectedCertificate.revokedAt).toLocaleDateString()}</td>
                        </tr>
                        <tr>
                          <td><strong>Revocation Reason:</strong></td>
                          <td>{selectedCertificate.revocationReason || 'N/A'}</td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {selectedCertificate && (
            <div className="mt-3">
              <h6 className="text-primary">Organization Details</h6>
              <div className="row">
                <div className="col-md-6">
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td><strong>Organization:</strong></td>
                        <td>{selectedCertificate.organizationName || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td><strong>Organizational Unit:</strong></td>
                        <td>{selectedCertificate.organizationalUnit || 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="col-md-6">
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td><strong>Country:</strong></td>
                        <td>{selectedCertificate.country || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td><strong>State/Province:</strong></td>
                        <td>{selectedCertificate.state || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td><strong>City/Locality:</strong></td>
                        <td>{selectedCertificate.locality || 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {selectedCertificate?.subjectAlternativeNames && selectedCertificate.subjectAlternativeNames.length > 0 && (
            <div className="mt-3">
              <h6 className="text-primary">Subject Alternative Names (SANs)</h6>
              <div className="bg-light p-2 rounded">
                {selectedCertificate.subjectAlternativeNames.map((san, index) => (
                  <Badge key={index} bg="secondary" className="me-1 mb-1">
                    {san}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {selectedCertificate?.keyUsage && selectedCertificate.keyUsage.length > 0 && (
            <div className="mt-3">
              <h6 className="text-primary">Key Usage</h6>
              <div className="bg-light p-2 rounded">
                {selectedCertificate.keyUsage.map((usage, index) => (
                  <Badge key={index} bg="info" className="me-1 mb-1">
                    {usage}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
          {selectedCertificate && (
            <div>
              <Button 
                variant="primary" 
                className="me-2"
                onClick={() => handleDownloadCertificate(selectedCertificate)}
              >
                <i className="fas fa-download mr-1"></i>
                Download
              </Button>
              {selectedCertificate.status === 'active' && (
                <Button 
                  variant="danger"
                  onClick={() => {
                    setShowViewModal(false);
                    handleRevokeCertificate(selectedCertificate);
                  }}
                >
                  <i className="fas fa-ban mr-1"></i>
                  Revoke
                </Button>
              )}
            </div>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}